"use client";

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

type TurnstileObject = {
    render: (
        container: string | HTMLElement,
        options: {
            sitekey: string;
            callback?: (token: string) => void;
            "error-callback"?: () => void;
            "expired-callback"?: () => void;
            theme?: "light" | "dark" | "auto";
            size?: "normal" | "flexible" | "compact";
        },
    ) => string;
    reset: (widgetId?: string | HTMLElement) => void;
    remove: (widgetId?: string | HTMLElement) => void;
};

declare global {
    interface Window {
        turnstile?: TurnstileObject;
    }
}

type TurnstileWidgetProps = {
    onTokenChange?: (token: string) => void;
};

export type TurnstileHandle = { reset: () => void };

export const TurnstileWidget = forwardRef<TurnstileHandle, TurnstileWidgetProps>(
    function TurnstileWidget({ onTokenChange }, ref) {
        const widgetRef = useRef<HTMLDivElement>(null);
        const widgetIdRef = useRef<string | null>(null);
        const isDisabled = import.meta.env.DEV && import.meta.env.VITE_TURNSTILE_ENABLED !== "true";

        useImperativeHandle(ref, () => ({
            reset: () => {
                if (widgetIdRef.current && window.turnstile) {
                    window.turnstile.reset(widgetIdRef.current);
                }
            },
        }));

        useEffect(() => {
            if (isDisabled) {
                onTokenChange?.("dev");
                return;
            }

            const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
            if (!siteKey) {
                console.error("Turnstile site key is missing");
                return;
            }

            const renderWidget = () => {
                if (!widgetRef.current || !window.turnstile) return;
                const turnstile = window.turnstile as TurnstileObject;

                if (widgetIdRef.current) {
                    turnstile.remove(widgetIdRef.current);
                }

                widgetIdRef.current = turnstile.render(widgetRef.current, {
                    sitekey: siteKey,
                    callback: (token: string) => onTokenChange?.(token),
                    "expired-callback": () => onTokenChange?.(""),
                    "error-callback": () => onTokenChange?.(""),
                });
            };

            const scriptId = "cloudflare-turnstile-script";
            let script = document.getElementById(scriptId) as HTMLScriptElement | null;

            if (!script) {
                script = document.createElement("script");
                script.id = scriptId;
                script.src =
                    "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
                script.async = true;
                script.defer = true;
                script.onload = renderWidget;
                document.head.appendChild(script);
            } else if (window.turnstile) {
                renderWidget();
            } else {
                script.addEventListener("load", renderWidget);
            }

            return () => {
                if (widgetIdRef.current && window.turnstile) {
                    (window.turnstile as TurnstileObject).remove(widgetIdRef.current);
                    widgetIdRef.current = null;
                }
                script?.removeEventListener("load", renderWidget);
            };
        }, [isDisabled, onTokenChange]);

        if (isDisabled) {
            return null;
        }

        return <div ref={widgetRef} />;
    },
);

export const useTurnstile = () => {
    const [captchaToken, setCaptchaToken] = useState<string>("");

    const resetTurnstile = () => {
        setCaptchaToken("");
    };

    return { captchaToken, setCaptchaToken, resetTurnstile };
};
